import { useCallback, useMemo } from "react";
import {
  ACCESS_LABELS,
  USER_INTERACTION_EVENT,
} from "@zapier/canvas-constants";

import { EventAssetType } from "./types";
import { uuid } from "./utils/uuid";

/**
 * @deprecated This is a legacy pattern for emitting user interaction events.
 *
 * For new event tracking, use atom setters from `@zapier/canvas-state/src/events/index.ts` instead.
 *
 * Example modern approach:
 * ```tsx
 * import { emitUserInteractionEventSetter } from '@zapier/canvas-state';
 * const emitUserInteractionEvent = useSetAtom(emitUserInteractionEventSetter);
 * await emitUserInteractionEvent({
 *   eventAction: "click",
 *   interactionGoal: "your goal",
 *   rawText: "button text",
 *   optionSelected: "option",
 * });
 * ```
 */
type Permission = (typeof ACCESS_LABELS)["editor" | "viewer" | "no access"];
export type SelectablePermission = "viewer" | "editor";
export type AssetType = "zap" | "interface" | "table" | "chatbot" | "canvas";
export function useEmitUserInteractionEvents({
  canvasId,
  sessionId,
  currentAccountId,
}: {
  canvasId: string;
  sessionId: number | undefined;
  currentAccountId: number | undefined;
}) {
  const commonProps = useMemo(
    () => ({
      system_name: "canvas",
      canvas_id: canvasId,
      customuser_id: sessionId,
      account_id: currentAccountId,
    }),
    [canvasId, sessionId, currentAccountId],
  );

  const emitEventOnAddStep = useCallback(
    (eventAction?: string, rawText?: string) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            event_action: eventAction || "click",
            interaction_goal: "Add step",
            interaction_raw_text: rawText || "Add step",
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  const emitEventOnAddPath = useCallback(
    (eventAction?: string, rawText?: string) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            event_action: eventAction || "click",
            interaction_goal: "Add split path",
            interaction_raw_text: rawText || "Split path",
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  const emitEventOnZapCreation = useCallback(
    (eventId: string) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: eventId,
            event_action: "click",
            interaction_goal: "Create zap draft",
            interaction_raw_text: "Create this Zap",
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  const emitEventOnZapRecreation = useCallback(
    (eventId: string) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: eventId,
            event_action: "click",
            interaction_goal: "Re-create zap draft",
            interaction_raw_text: "Re-create this Zap",
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  const emitEventOnDownload = useCallback(
    (rawText: string) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            event_action: "click",
            interaction_goal: "Download canvas",
            interaction_raw_text: rawText,
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  const emitEventOnShareModalOpen = useCallback(() => {
    if (window.EventToolkit?.emit) {
      window.EventToolkit.emit(
        {
          event_id: uuid(),
          event_action: "click",
          interaction_goal: "Open sharing modal",
          interaction_raw_text: "Share",
          ...commonProps,
        },
        USER_INTERACTION_EVENT,
      );
    }
  }, [commonProps]);

  // When we change account-level sharing settings
  const emitEventOnShareCanvas = useCallback(
    (permission: Permission) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            event_action: "click",
            interaction_goal: "Update sharing",
            interaction_raw_text: `${permission} - account`,
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  // When we change user-level sharing settings
  const emitEventOnShareCanvasWithUser = useCallback(
    (permission: SelectablePermission) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            event_action: "click",
            interaction_goal: "Update sharing",
            interaction_raw_text: `${permission} - individual`,
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  const emitEventOnShareCanvasWithTeam = useCallback(
    (permission: SelectablePermission) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            event_action: "click",
            interaction_goal: "Update sharing",
            interaction_raw_text: `${permission} - team`,
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  const emitEventOnHelpClick = useCallback(() => {
    if (window.EventToolkit?.emit) {
      window.EventToolkit.emit(
        {
          event_id: uuid(),
          event_action: "click",
          interaction_goal: "Help",
          interaction_raw_text: "Help",
          ...commonProps,
        },
        USER_INTERACTION_EVENT,
      );
    }
  }, [commonProps]);

  const emitEventOnShareUpsell = useCallback(() => {
    if (window.EventToolkit?.emit) {
      window.EventToolkit.emit(
        {
          event_id: uuid(),
          event_action: "click",
          interaction_goal: "upsell",
          interaction_raw_text: "Upgrade to enable sharing",
          ...commonProps,
        },
        USER_INTERACTION_EVENT,
      );
    }
  }, [commonProps]);

  const emitEventOnAddingAssetToProject = useCallback(
    (assetType: AssetType, rawText: string) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            event_action: "click",
            interaction_goal: `Add ${assetType} to a Project`,
            interaction_raw_text: rawText,
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  const emitEventOnDeleteCanvas = useCallback(() => {
    if (window.EventToolkit?.emit) {
      window.EventToolkit.emit(
        {
          event_id: uuid(),
          event_action: "click",
          interaction_goal: "delete",
          interaction_raw_text: "Delete",
          ...commonProps,
        },
        USER_INTERACTION_EVENT,
      );
    }
  }, [commonProps]);

  const emitEventOnPublicLinkSharing = useCallback(
    (is_public: boolean) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            event_action: "click",
            interaction_goal: "public URL",
            interaction_raw_text: is_public
              ? "Generate public URL"
              : "Disabled public link sharing",
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  const emitEventOnSelectTemplate = useCallback(
    (slug: string) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            event_action: "click",
            interaction_goal: "Preview template",
            interaction_raw_text: slug,
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  const emitEventOnAIPrompt = useCallback(
    ({ prompt }: { prompt: string }) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            event_action: "click",
            interaction_goal: "AI Prompt",
            interaction_raw_text: prompt,
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  const emitEventOnAssetOpen = useCallback(
    ({
      asset,
      location,
    }: {
      asset: EventAssetType;
      location: "node" | "sidebar" | "tour" | "focus mode";
    }) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            event_action: "click",
            interaction_goal: "Open Asset",
            option_selected: asset,
            interaction_raw_text: location,
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  const emitEventOnCopilotMessageSent = useCallback(() => {
    if (window.EventToolkit?.emit) {
      window.EventToolkit.emit(
        {
          event_id: uuid(),
          event_action: "click",
          interaction_goal: "Send message",
          interaction_raw_text: "Canvas copilot message sent",
          ...commonProps,
        },
        USER_INTERACTION_EVENT,
      );
    }
  }, [commonProps]);

  const emitEventOnAutoLayout = useCallback(
    ({ forAllSteps }: { forAllSteps: boolean }) => {
      if (window.EventToolkit?.emit) {
        window.EventToolkit.emit(
          {
            event_id: uuid(),
            event_action: "click",
            interaction_goal: "Auto layout",
            interaction_raw_text: forAllSteps
              ? "Clean up layout"
              : "Clean up layout for selected steps",
            ...commonProps,
          },
          USER_INTERACTION_EVENT,
        );
      }
    },
    [commonProps],
  );

  return {
    emitEventOnAddStep,
    emitEventOnAddPath,
    emitEventOnZapCreation,
    emitEventOnZapRecreation,
    emitEventOnDownload,
    emitEventOnShareCanvas,
    emitEventOnShareCanvasWithUser,
    emitEventOnShareCanvasWithTeam,
    emitEventOnHelpClick,
    emitEventOnShareUpsell,
    emitEventOnAddingAssetToProject,
    emitEventOnDeleteCanvas,
    emitEventOnPublicLinkSharing,
    emitEventOnSelectTemplate,
    emitEventOnAIPrompt,
    emitEventOnShareModalOpen,
    emitEventOnAutoLayout,
    emitEventOnAssetOpen,
    emitEventOnCopilotMessageSent,
  };
}
